<template>
    <div id="mci-app-signup">
        <div v-if="state.catalogError" role="alert" class="signup-banner--error">
            <p>Sorry — we couldn't load the signup form. Please try again later or contact us directly.</p>
        </div>

        <div v-else-if="!state.catalogLoaded">
            <p>Loading…</p>
        </div>

        <div v-else-if="state.submitted">
            <success />
        </div>

        <div v-else>
            <p class="signup-step__meta">Step {{ state.currentStep }} of {{ state.totalSteps }}</p>

            <div class="signup-step">
                <Transition :name="`signup-slide-${state.stepDirection}`" mode="out-in">
                    <step-plan v-if="state.currentStep === 1" key="1" />
                    <step-contact v-else-if="state.currentStep === 2" key="2" />
                    <step-brokerage v-else-if="state.currentStep === 3" key="3" />
                    <step-associations v-else-if="state.currentStep === 4" key="4" />
                    <step-review v-else-if="state.currentStep === 5" key="5" />
                </Transition>
            </div>

            <div class="signup-actions">
                <button v-if="state.currentStep > 1" type="button" class="signup-button" @click="goBack">Back</button>

                <button
                    v-if="state.currentStep < state.totalSteps"
                    type="button"
                    class="signup-button signup-button--primary"
                    :disabled="!stepIsValid(state.currentStep)"
                    @click="goNext"
                >
                    Next
                </button>

                <button
                    v-else
                    type="button"
                    class="signup-button signup-button--primary"
                    :disabled="!stepIsValid(state.currentStep) || state.submitting"
                    @click="submitForm"
                >
                    {{ state.submitting ? 'Submitting…' : 'Submit' }}
                </button>
            </div>

            <quote-panel />
        </div>
    </div>
</template>

<script setup>
import { onMounted } from 'vue';
import useState from '@/composables/useState';
import useApi from '@/composables/useApi';

import StepPlan from '@/components/StepPlan.vue';
import StepContact from '@/components/StepContact.vue';
import StepBrokerage from '@/components/StepBrokerage.vue';
import StepAssociations from '@/components/StepAssociations.vue';
import StepReview from '@/components/StepReview.vue';
import QuotePanel from '@/components/QuotePanel.vue';
import Success from '@/components/Success.vue';

const { state, stepIsValid, goNext, goBack } = useState();
const api = useApi();

////////////////////////////////////////////////////////////////////
/// Load the public catalog (associations + plans + addons) on mount.
onMounted(async () => {
    try {
        const catalog = await api.loadCatalog();
        state.catalog = catalog;
        state.catalogLoaded = true;
    } catch (err) {
        state.catalogError = err.message || 'Failed to load catalog';
    }
});

////////////////////////////////////////////////////////////////////
/// Submit the form. Maps 422 server errors back into state.serverErrors
/// so each step component can display them inline.
const submitForm = async () => {
    state.submitting = true;
    state.submitError = null;
    state.serverErrors = {};

    try {
        await api.submit(state.form);
        state.submitted = true;
    } catch (err) {
        if (err.response?.status === 422) {
            state.serverErrors = err.response.data.errors || {};
            state.submitError = 'Please fix the highlighted fields and try again.';
            // If errors map back to earlier steps, send the user there.
            jumpToFirstErrorStep();
        } else if (err.response?.status === 403) {
            state.submitError = 'This signup form is not allowed on this site.';
        } else {
            state.submitError = 'Something went wrong. Please try again or contact us directly.';
        }
    } finally {
        state.submitting = false;
    }
};

////////////////////////////////////////////////////////////////////
/// Map server-error field names back to step indexes so we can route
/// the user to the earliest step containing an error.
const jumpToFirstErrorStep = () => {
    const fields = Object.keys(state.serverErrors);
    const stepFor = (field) => {
        if (['planCode', 'addonCodes'].some((f) => field.startsWith(f))) return 1;
        if (['name', 'email', 'phone', 'companyWebsite'].some((f) => field.startsWith(f))) return 2;
        if (['brokerage', 'principalBrokerName', 'principalBrokerEmail'].some((f) => field.startsWith(f))) return 3;
        if (field.startsWith('associations')) return 4;
        return null;
    };
    const earliest = fields.map(stepFor).filter(Boolean).sort()[0];
    if (earliest) state.currentStep = earliest;
};
</script>
